import React from 'react';
import {
  StyleSheet,
  Text,
  TextInput,
  View,
  TouchableOpacity,
  TextInputProps,
  TouchableWithoutFeedback,
} from 'react-native';
import { TextInputMask, TextInputMaskOptionProp, TextInputMaskTypeProp } from 'react-native-masked-text';
import Icon from './Icon';
import { Theme } from '@enum/theme';
import SpaceH from './SpaceH';

interface InputIconProps {
  label?: string;
  mask?: TextInputMaskTypeProp;
  iconSet?: string;
  maskOptions?: TextInputMaskOptionProp;
  icon?: string;
  handleIcon?: () => void;
  handle?: (text: string, raw: string) => void;
  maxLength?: number;
  numberOfLines?: number;
  secureTextEntry?: boolean;
  placeholder?: string;
  value?: string | null;
  onlyText?: boolean;
  iconSize?: number;
  keyboardType?: TextInputProps['keyboardType'];
  autoCapitalize?: TextInputProps['autoCapitalize'];
  multiLine?: boolean;
  iconSide?: 'left' | 'right';
}

type InputType = React.ElementType;

const TextInputWithIcon: React.FC<InputIconProps> = ({
  label,
  mask,
  maskOptions,
  icon,
  handleIcon,
  handle,
  iconSet,
  maxLength,
  numberOfLines,
  secureTextEntry,
  placeholder,
  value,
  onlyText,
  iconSize,
  keyboardType,
  autoCapitalize,
  iconSide = 'right',
  multiLine,
}) => {
  const inputRef = React.useRef<TextInput>(null);

  const styles = StyleSheet.create({
    container: {
      backgroundColor: '#fff',
      borderColor: Theme.border,
      borderStyle: 'solid',
      borderWidth: 1,
      borderRadius: 8,
      padding: 3,
    },
    inputContainer: {
      flexDirection: multiLine ? 'column' : 'row',
    },
    inputField: {
      height: 36,
      width: icon ? '88%' : '100%',
      padding: 5,
      color: Theme.primary,
      fontSize: 16,
    },
    inputFieldMultilines: {
      height: 100,
      width: '100%',
      fontSize: 16,
      padding: 5,
      minHeight: 100,
      color: Theme.primary,
      textAlignVertical: 'top',
    },
    label: {
      top: 3,
      left: 5,
      color: Theme.primary,
      fontSize: 10,
    },
    iconButton: {
      top: -3,
      justifyContent: 'center',
      minWidth: 50,
      alignItems: 'center',
      zIndex: 200,
    },
  });

  const handleText = (maskedValue: string, rawValue: string) => {
    if (handle) {
      handle(maskedValue, rawValue);
    }
  };

  const InputForUse: InputType = mask ? TextInputMask : TextInput;

  return (

    <View style={styles.container}>
      {label && <Text style={styles.label}>{label}:</Text>}
      {onlyText && !label && <View style={{ height: 10 }} />}
      <View style={[styles.inputContainer, multiLine && { alignItems: 'flex-start' }]}>
        {icon && iconSide === 'left' && (
          <TouchableOpacity style={styles.iconButton} onPress={() => handleIcon && handleIcon()}>
            <>
              <SpaceH size={7} />
              <Icon
                iconSet={iconSet || undefined}
                size={iconSize || 22}
                color={Theme.primary}
                name={icon}
              />
            </>
          </TouchableOpacity>
        )}
        <TouchableWithoutFeedback onPress={() => inputRef?.current?.focus()}>
          <InputForUse
            ref={inputRef}
            editable={!onlyText}
            includeRawValueInChangeText
            type={mask ? mask : null}
            options={maskOptions || undefined}
            maxLength={maxLength || undefined}
            multiline={multiLine}
            numberOfLines={numberOfLines}
            keyboardType={keyboardType || 'default'}
            style={multiLine ? styles.inputFieldMultilines : styles.inputField}
            placeholder={placeholder || ''}
            autoCapitalize={autoCapitalize || 'none'}
            autoCorrect={false}
            placeholderTextColor="rgba(0, 0, 0, .3)"
            textContentType="newPassword"
            secureTextEntry={secureTextEntry}
            value={value ? String(value) : ''}
            enablesReturnKeyAutomatically
            onChangeText={handleText}
          />
        </TouchableWithoutFeedback>

        {icon && iconSide === 'right' && (
          <TouchableOpacity style={styles.iconButton} onPress={() => handleIcon && handleIcon()}>
            <Icon
              iconSet={iconSet || undefined}
              size={iconSize || 22}
              color={Theme.primary}
              name={icon}
            />
          </TouchableOpacity>
        )}
      </View>
    </View>

  );
};

export default TextInputWithIcon;
